import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkedStepsScene } from '@/types/gameSpec';
import { normalize, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

/**
 * Reveal-and-confirm walkthrough. The learner advances each step
 * (optionally typing a `check` value) and finally enters the answer.
 */
export const WorkedStepsSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<WorkedStepsScene>) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [checkValues, setCheckValues] = useState<Record<number, string>>({});
  const [finalAnswer, setFinalAnswer] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const currentStep = scene.steps[stepIdx];
  const isLastStep = stepIdx === scene.steps.length - 1;

  const advance = () => {
    if (currentStep.check) {
      const v = normalize(checkValues[stepIdx] ?? '', false);
      if (v !== normalize(currentStep.check, false)) {
        // wrong check — block advance with a soft hint
        return;
      }
    }
    if (!isLastStep) setStepIdx(stepIdx + 1);
  };

  const submit = () => {
    if (revealed) return;
    const ok = normalize(finalAnswer, false) === normalize(scene.final_answer, false);
    setIsCorrect(ok);
    setRevealed(true);
    setTimeout(() => onAnswer(ok, { final_answer: finalAnswer }), 800);
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.worked_steps}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        <ol className="space-y-2">
          {scene.steps.map((s, i) => {
            const visible = i <= stepIdx;
            return (
              <li
                key={i}
                className={cn(
                  'rounded-md border px-3 py-2 transition',
                  visible ? 'bg-card' : 'bg-muted/40 text-muted-foreground italic',
                )}
              >
                <div className="flex items-start gap-2">
                  <span className="text-sm text-muted-foreground">
                    Step {i + 1}.
                  </span>
                  <span className="flex-1">{visible ? s.text : '— locked —'}</span>
                  {visible && i < stepIdx && <Check className="h-4 w-4 text-green-600" />}
                </div>
                {visible && i === stepIdx && s.check && (
                  <div className="mt-2 flex gap-2">
                    <Input
                      placeholder="Enter check value"
                      value={checkValues[i] ?? ''}
                      onChange={(e) =>
                        setCheckValues({ ...checkValues, [i]: e.target.value })
                      }
                      className="max-w-xs"
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        {!isLastStep && (
          <Button onClick={advance}>Next step</Button>
        )}

        {isLastStep && !revealed && (
          <div className="space-y-2 pt-2 border-t">
            <label className="text-sm font-medium">Final answer</label>
            <Input
              value={finalAnswer}
              onChange={(e) => setFinalAnswer(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
            <Button onClick={submit} disabled={!finalAnswer.trim()}>
              Submit answer
            </Button>
          </div>
        )}

        {revealed && (
          <p
            className={cn(
              'font-medium',
              isCorrect ? 'text-green-700' : 'text-destructive',
            )}
          >
            {isCorrect ? 'Correct!' : `Answer: ${scene.final_answer}`}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
